import { PencilLine, Trash2 } from 'lucide-react';

import { formatDate, formatTimeRange } from '@/lib/format';
import { cn } from '@/lib/utils';
import { AUDIENCE_LABEL, CATEGORY, CLASS_SCOPE_LABEL, MONTHS } from '../constants';
import type { CalendarEventItem } from '../types/calendar';

export interface EventRosterProps {
  events: CalendarEventItem[];
  onSelect: (item: CalendarEventItem) => void;
  /** Both absent on a read-only calendar, which then carries no actions. */
  onEdit?: (item: CalendarEventItem) => void;
  onRemove?: (item: CalendarEventItem) => void;
}

/**
 * Every entry in date order.
 *
 * A timeline rather than a table: these are dated things in sequence, and the
 * rail down the side is what makes the gaps between them readable.
 */
export function EventRoster({ events, onSelect, onEdit, onRemove }: EventRosterProps) {
  return (
    <ol className="relative flex flex-col gap-3 pl-6">
      <span
        className="absolute bottom-4 left-3 top-4 w-[2px] bg-gradient-to-b from-indigo-400 via-slate-300 dark:via-slate-700 to-transparent"
        aria-hidden
      />

      {events.map((item) => {
        const style = CATEGORY[item.category];
        const Icon = style.icon;
        const days = item.days;

        return (
          <li key={item.uuid} className="relative">
            <span
              className={cn(
                'absolute -left-[18px] top-5 h-3.5 w-3.5 rounded-full border-2 border-white dark:border-[#111c2e] shadow-2xs z-10',
                style.dot,
              )}
              aria-hidden
            />

            <div className="flex flex-col gap-3 rounded-2xl border border-line bg-surface-2 p-4 transition-all hover:border-accent-line sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={() => onSelect(item)}
                aria-label={`Open ${item.title}`}
                className={cn(
                  'grid h-14 w-14 shrink-0 cursor-pointer place-items-center rounded-2xl border text-center transition-transform hover:scale-105',
                  style.chip,
                )}
              >
                <span className="block text-lg font-black leading-none">
                  {item.startDate.slice(8, 10)}
                </span>
                <span className="block text-[9px] font-black uppercase tracking-wider">
                  {MONTHS[Number(item.startDate.slice(5, 7)) - 1]?.slice(0, 3)}
                </span>
              </button>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => onSelect(item)}
                    className="cursor-pointer text-left text-[13.5px] font-bold text-ink hover:text-accent"
                  >
                    {item.title}
                  </button>
                  <span
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-wider',
                      style.chip,
                    )}
                  >
                    <Icon className="h-3 w-3" aria-hidden />
                    {style.label}
                  </span>
                  <span className="rounded-full border border-line bg-surface px-2 py-0.5 text-[10px] font-bold text-ink-2">
                    {CLASS_SCOPE_LABEL[item.classScope]}
                  </span>
                  <span className="rounded-full border border-line bg-surface px-2 py-0.5 text-[10px] font-bold text-ink-2">
                    {AUDIENCE_LABEL[item.audience]}
                  </span>
                  {days > 1 && (
                    <span className="rounded-full border border-line bg-surface px-2 py-0.5 font-mono text-[10px] font-bold text-ink-2">
                      {days} days
                    </span>
                  )}
                </div>

                {item.description && (
                  <p className="mt-1 text-xs font-medium text-ink-2">{item.description}</p>
                )}

                <p className="mt-1 font-mono text-[11px] font-semibold text-ink-3">
                  {formatDate(item.startDate)}
                  {item.endDate !== item.startDate && ` → ${formatDate(item.endDate)}`}
                  {' · '}
                  {formatTimeRange(item.startTime, item.endTime)}
                </p>
              </div>

              {/* A holiday is edited or withdrawn from its details — see
                  `EventDetailsModal`. Here it simply carries no actions. */}
              {item.source === 'HOLIDAY' ? (
                <span className="shrink-0 self-end text-[11px] font-bold text-ink-3 sm:self-auto">
                  Declared holiday
                </span>
              ) : onEdit && onRemove ? (
              <div className="flex shrink-0 items-center gap-1.5 self-end sm:self-auto">
                <button
                  type="button"
                  aria-label={`Edit ${item.title}`}
                  onClick={() => onEdit(item)}
                  className="inline-flex cursor-pointer items-center gap-1.5 rounded-xl border border-blue-200/80 dark:border-blue-900/50 bg-blue-50 dark:bg-blue-950/40 px-3 py-1.5 text-[12px] font-extrabold text-blue-700 dark:text-blue-300 transition-all hover:bg-blue-100 dark:hover:bg-blue-900/50"
                >
                  <PencilLine className="h-3.5 w-3.5" aria-hidden />
                  Edit
                </button>
                <button
                  type="button"
                  aria-label={`Remove ${item.title}`}
                  onClick={() => onRemove(item)}
                  className="grid h-8 w-8 cursor-pointer place-items-center rounded-xl border border-rose-200/80 dark:border-rose-900/50 bg-rose-50 dark:bg-rose-950/40 text-rose-500 dark:text-rose-400 transition-all hover:scale-105 hover:bg-rose-100"
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden />
                </button>
              </div>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
